import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { Field, Select, StatusPill, formatDateTime } from '@/components/live';
import { ApprovalPinField, PIN_RESULTS, isPinResult } from '@/components/ApprovalPin';
import { DropZone } from '@/components/DropZone';
import { callRpc } from '@/api/resources';
import { bucketRefusal, signedUrlsFor, uploadDocument } from '@/api/documents';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin } from '@/auth/session';
import { NameMatchBasisSelect } from '@/components/NameMatchBasisSelect';
import { toast } from '@/lib/toast';

export interface NameVerificationRow {
  id: string;
  verification_code: string | null;
  entity_table: string;
  party_id: string | null;
  party_name: string | null;
  farmer_id: string | null;
  farmer_code: string | null;
  farmer_name: string | null;
  kyc_record_id: string | null;
  kyc_type_label: string | null;
  masked_document_no: string | null;
  land_document_id: string | null;
  land_document_type: string | null;
  land_document_no: string | null;
  plot_code: string | null;
  village_name: string | null;
  conflict_type: 'id_vs_name' | 'kyc_document_vs_id' | 'land_document_vs_id' | 'land_record_vs_id';
  name_given: string | null;
  name_as_per_id: string | null;
  name_on_document: string | null;
  basis: string | null;
  remarks: string | null;
  verification_status: 'open' | 'accepted' | 'rejected' | 'resolved';
  decided_by_name: string | null;
  decided_at: string | null;
  decision_remarks: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  /** The proof (migration 0166): which document shows the names are one person, and its scans. */
  matching_document_type: string | null;
  matching_document_no: string | null;
  evidence_document_ids: string[] | null;
}

/** What can show two names belong to one person (owner, 2026-09-27: "Md sheik ... Mohammed Sheik"). */
export const MATCHING_DOCUMENTS = [
  { id: 'aadhaar', label: 'Aadhaar' }, { id: 'voter_id', label: 'Voter ID (EPIC)' }, { id: 'ration_card', label: 'Ration card' },
  { id: 'pan', label: 'PAN card' }, { id: 'passport', label: 'Passport' }, { id: 'driving_licence', label: 'Driving licence' },
  { id: 'bank_passbook', label: 'Bank passbook' }, { id: 'affidavit', label: 'Affidavit (one and the same person)' },
  { id: 'gazette', label: 'Gazette notification (name change)' }, { id: 'land_record', label: 'Another land record' },
  { id: 'other', label: 'Other (say in remarks)' },
];

export const CONFLICT_LABEL = {
  id_vs_name: 'ID card name vs name given',
  kyc_document_vs_id: 'KYC document vs ID card name',
  land_document_vs_id: 'Land document vs ID card name',
  land_record_vs_id: 'Land record vs ID card name',
} as const;

/** The document a verification is about: the KYC record or land document, or the party itself. */
export function documentOf(row: NameVerificationRow): string {
  if (row.kyc_record_id) return `${row.kyc_type_label ?? 'KYC'} ${row.masked_document_no ?? ''}`.trim();
  if (row.land_document_id) return `${row.land_document_type ?? 'Land document'}${row.land_document_no ? ` ${row.land_document_no}` : ''}${row.plot_code ? ` (${row.plot_code})` : ''}`;
  return 'Party record';
}

/**
 * One verification: the names compared, why they are the same person, the proof, and - for someone
 * other than the maker - accept or reject. Shown where the document lives: the plot form's Name
 * check tab and land document list, and the farmer's KYC tab (the Name verifications page was
 * dropped, owner 2026-09-27: "we are concerned only with the KYC and land ownership docs").
 */
export function NameVerificationPanel({ row, onDone }: { row: NameVerificationRow; onDone?: () => void }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [basis, setBasis] = useState(row.basis ?? '');
  const [remarks, setRemarks] = useState(row.remarks ?? '');
  const [matchingType, setMatchingType] = useState(row.matching_document_type ?? '');
  const [matchingNo, setMatchingNo] = useState(row.matching_document_no ?? '');
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [decisionRemarks, setDecisionRemarks] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const decidable = row.verification_status === 'open';
  const mayDecide = (isAdmin(profile) || can(profile, 'farmer.register', 'approve')) && row.created_by !== profile?.user.id;
  // The maker gathers the proof as much as the approver does; only the decision is the approver's.
  const mayRecord = decidable && (isAdmin(profile) || can(profile, 'farmer.register', 'update') || can(profile, 'farmer.register', 'approve'));
  const storedFiles = row.evidence_document_ids ?? [];
  const links = useQuery({
    queryKey: ['signed-urls', ...storedFiles], queryFn: () => signedUrlsFor(storedFiles), enabled: storedFiles.length > 0,
  });
  const proofChanged = matchingType !== (row.matching_document_type ?? '') || matchingNo.trim() !== (row.matching_document_no ?? '') || newFiles.length > 0;

  function pickFile(file: File) {
    const refusal = bucketRefusal('kyc-documents', file);
    if (refusal) { toast.error(`${file.name}: ${refusal}`); return; }
    setNewFiles((now) => now.some((other) => other.name === file.name && other.size === file.size) ? now : [...now, file]);
  }

  /** Uploads any new files and records the proof. Returns false when it could not. */
  async function saveProof(): Promise<boolean> {
    if (!proofChanged) return true;
    if (!profile?.organization) return false;
    const ids = [...storedFiles];
    for (const file of newFiles) {
      const uploaded = await uploadDocument({ file, bucket: 'kyc-documents', organizationId: profile.organization.id, ownerId: row.party_id ?? row.id });
      ids.push(uploaded.id);
    }
    await callRpc('fn_set_name_verification_evidence', {
      p_verification_id: row.id, p_matching_document_type: matchingType || null,
      p_matching_document_no: matchingNo.trim() || null, p_evidence_document_ids: ids,
    });
    setNewFiles([]);
    await queryClient.invalidateQueries({ queryKey: ['name_verifications'] });
    return true;
  }

  async function recordProof() {
    setBusy(true);
    try {
      if (await saveProof()) toast.success('Proof recorded. Someone with approve rights accepts or rejects it.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The proof could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: 'accepted' | 'rejected') {
    const problems = [
      !basis && 'Choose what confirms (or fails to confirm) the name.',
      !remarks.trim() && 'Add remarks on how it was checked.',
      decision === 'accepted' && !matchingType && 'Say which document shows the two names are the same person.',
      !pin && 'Enter your approval PIN.',
    ].filter(Boolean) as string[];
    if (problems.length) { toast.errors('Before deciding', problems); return; }
    setBusy(true);
    try {
      if (!(await saveProof())) return;
      const outcome = await callRpc<string>('fn_decide_name_verification', {
        p_verification_id: row.id, p_decision: decision, p_basis: basis, p_remarks: remarks.trim(),
        p_decision_remarks: decisionRemarks.trim() || null, p_pin: pin,
      });
      if (isPinResult(outcome)) { toast.error(PIN_RESULTS[outcome] ?? outcome); setPin(''); return; }
      toast.success(decision === 'accepted' ? 'Name accepted.' : 'Name rejected.');
      await queryClient.invalidateQueries({ queryKey: ['name_verifications'] });
      onDone?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="name-verification-panel">
      <dl className="detail-grid">
        <div><dt>Status</dt><dd><StatusPill value={row.verification_status} /></dd></div>
        <div><dt>Name given</dt><dd>{row.name_given ?? '--'}</dd></div>
        <div><dt>Name as per ID card</dt><dd>{row.name_as_per_id ?? '--'}</dd></div>
        <div><dt>Document</dt><dd>{documentOf(row)}</dd></div>
        {row.conflict_type !== 'id_vs_name' && <div><dt>Name on the document</dt><dd>{row.name_on_document ?? '--'}</dd></div>}
        <div><dt>Raised</dt><dd>{formatDateTime(row.created_at)}{row.created_by_name ? ` by ${row.created_by_name}` : ''}</dd></div>
        {row.decided_at && <div><dt>Decided</dt><dd>{formatDateTime(row.decided_at)}{row.decided_by_name ? ` by ${row.decided_by_name}` : ''}</dd></div>}
        {row.decision_remarks && <div><dt>Decision remarks</dt><dd>{row.decision_remarks}</dd></div>}
      </dl>
      <Field label="Confirmed by" required={decidable}>
        <NameMatchBasisSelect disabled={!decidable || !mayDecide} onChange={setBasis} placeholder="What confirms it is the same person"
                              recorded={row.conflict_type === 'id_vs_name' ? row.name_given : row.name_on_document} reference={row.name_as_per_id} value={basis} />
      </Field>
      <Field label="Remarks" required={decidable}>
        <textarea disabled={!decidable || !mayDecide} onChange={(event) => setRemarks(event.target.value)} rows={2}
                  placeholder="e.g. Voter ID and ration card both read 'Md. Sheik'; father's name matches" value={remarks} />
      </Field>

      <div className="section-heading"><h3>Proof</h3></div>
      <div className="field-row">
        <Field hint="The document that shows both names belong to one person. Needed to accept." label="Matching document">
          <Select disabled={!mayRecord} onChange={setMatchingType} options={MATCHING_DOCUMENTS} placeholder="Select document" value={matchingType} />
        </Field>
        <Field label="Its number">
          <input disabled={!mayRecord} onChange={(event) => setMatchingNo(event.target.value)} value={matchingNo} />
        </Field>
      </div>
      {storedFiles.length > 0 && (
        <p className="muted" style={{ margin: 0 }}>
          Supporting files:{' '}
          {storedFiles.map((id, index) => (
            links.data?.[id]
              ? <a href={links.data[id]} key={id} rel="noreferrer" target="_blank">File {index + 1}</a>
              : <span key={id}>File {index + 1}</span>
          )).reduce<ReactNode[]>((all, link, index) => (index === 0 ? [link] : [...all, ' · ', link]), [])}
        </p>
      )}
      {mayRecord && (
        <Field hint="Optional. Scans of the matching document, an affidavit, a gazette notice ..." label="Supporting files">
          <DropZone accept="image/*,application/pdf" buttonLabel={newFiles.length ? 'Add more files' : 'Choose files'}
                    label="Drag the files here, or" multiple onFile={pickFile} />
        </Field>
      )}
      {newFiles.length > 0 && (
        <ul className="land-document-list">
          {newFiles.map((file) => (
            <li key={`${file.name}-${file.size}`}>
              <span className="land-document-text"><strong>{file.name}</strong><small className="muted">uploaded when recorded</small></span>
              <button onClick={() => setNewFiles((now) => now.filter((other) => other !== file))} type="button"><X size={14} /> Remove</button>
            </li>
          ))}
        </ul>
      )}

      {decidable && mayDecide && (
        <>
          <Field label="Decision remarks">
            <input onChange={(event) => setDecisionRemarks(event.target.value)} value={decisionRemarks} />
          </Field>
          <ApprovalPinField onChange={setPin} value={pin} />
        </>
      )}
      {decidable && !mayDecide && (
        <p className="muted" style={{ margin: 0 }}>
          {row.created_by === profile?.user.id ? 'You recorded this name; someone else accepts or rejects it.' : 'Accepting a name needs approve rights on the farmer register.'}
        </p>
      )}
      {decidable && (mayRecord || mayDecide) && (
        <div className="inline-form-actions">
          {mayRecord && !mayDecide && (
            <button className="primary" disabled={busy || !proofChanged} onClick={() => void recordProof()} type="button">
              {busy ? 'Saving...' : 'Record proof'}
            </button>
          )}
          {mayDecide && (
            <>
              <button className="danger" disabled={busy} onClick={() => void decide('rejected')} type="button"><X size={15} /> Reject</button>
              <button className="primary" disabled={busy} onClick={() => void decide('accepted')} type="button"><Check size={15} /> Accept</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
